import { useState, useEffect, useCallback } from 'react';
import { 
  Phone, 
  PhoneIncoming, 
  PhoneOutgoing, 
  Mic, 
  MicOff, 
  Square, 
  Pause, 
  Play, 
  RefreshCw,
  PhoneOff,
  Clock,
  ArrowRight,
  Radio,
  Circle,
  AlertTriangle,
  Brain,
  TrendingUp,
  TrendingDown,
  Minus,
  Plus,
  Pencil,
  Trash2,
  Settings,
  Zap,
  MessageSquare,
  BarChart3,
  Activity,
  Save,
  X,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-frontend/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@easyflow/ui-frontend/components/ui/alert-dialog";
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useSocket } from '@/contexts/SocketContext';
import { usePermissions } from '@/contexts/PermissionContext';
import { apiClient } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';
import type {
  CoachingSentimentSettings,
  HangupAllCallsResult,
  VoipActiveCallListItem as LiveCall,
  CoachingRtaCard as RTACard,
  CoachingStats as CoachingStatsData,
  CoachingTrend as TrendData,
} from '@easyflow/shared';


interface CardTriggerEvent {
  card: RTACard;
  callUuid: string;
  matchedKeyword: string;
  transcriptSnippet: string;
  timestamp: string;
  ragContext?: string;
}

type SentimentLabel = 'positive' | 'neutral' | 'negative' | 'critical';

interface SentimentUpdateEvent {
  callUuid: string;
  role: string;
  score: number;
  label: SentimentLabel;
  textSnippet: string;
  timestamp: string;
}

interface CallSentimentState {
  score: number;
  label: SentimentLabel;
  history: { score: number; timestamp: string }[];
}

export default function LiveCalls() {
  const [liveCalls, setLiveCalls] = useState<LiveCall[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isTerminatingAll, setIsTerminatingAll] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const { isConnected, emit, on, off, joinRoom, leaveRoom, socketVersion } = useSocket();

  const [triggeredCards, setTriggeredCards] = useState<CardTriggerEvent[]>([]);
  const [callSentiments, setCallSentiments] = useState<Map<string, CallSentimentState>>(new Map());
  const [rtaCards, setRtaCards] = useState<RTACard[]>([]);
  const [isLoadingCards, setIsLoadingCards] = useState(false);
  const [editingCard, setEditingCard] = useState<RTACard | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [cardForm, setCardForm] = useState<{
    title: string;
    content: string;
    category: string;
    priority: number;
    keywords: string;
    keyword_mode: 'any' | 'all' | 'exact' | 'regex';
    language: string;
    cooldown_seconds: number;
    rag_enabled: boolean;
    target_queues_text: string;
  }>({
    title: '',
    content: '',
    category: 'general',
    priority: 1,
    keywords: '',
    keyword_mode: 'any',
    language: 'it',
    cooldown_seconds: 60,
    rag_enabled: false,
    target_queues_text: '',
  });
  const [coachingStats, setCoachingStats] = useState<CoachingStatsData | null>(null);
  const [sentimentSettings, setSentimentSettings] = useState<CoachingSentimentSettings>({
    sentiment_mode: 'dictionary',
    warning_threshold: 0.4,
    critical_threshold: 0.2,
    emit_interval_seconds: 5,
    notify_on_critical: true,
  });
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  const { hasPermission } = usePermissions();
  const hasCoachingPermission = hasPermission('live_coaching.access');

  useEffect(() => {
    if (!hasCoachingPermission || !isConnected || !user?.tenant_id) return;
    const room = `coaching:${user.tenant_id}`;
    joinRoom(room);
    return () => { leaveRoom(room); };
  }, [hasCoachingPermission, isConnected, user?.tenant_id, joinRoom, leaveRoom]);

  const loadActiveCalls = useCallback(async () => {
    if (!user) return;
    try {
      const data = await apiClient.get<LiveCall[]>('/voip/calls/active');
      if (data.success && Array.isArray(data.data)) {
        setLiveCalls(data.data);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('[LiveCalls] Failed to load active calls:', error);
      setIsLoading(false);
    }
  }, [user]);

  const handleTerminateAllCalls = async () => {
    if (!user || user.role !== 'admin') return;
    
    setIsTerminatingAll(true);
    try {
      const data = await apiClient.post<HangupAllCallsResult>('/calls/hangup-all', {});

      if (data.success && data.data) {
        toast({
          title: "Chiamate terminate",
          description: `${data.data.terminated} chiamate su ${data.data.total} sono state terminate`,
        });
        loadActiveCalls();
      } else {
        throw new Error(data.error || 'Errore durante la terminazione');
      }
    } catch (error) {
      toast({
        title: "Errore",
        description: getErrorMessage(error) || "Impossibile terminare le chiamate",
        variant: "destructive",
      });
    } finally {
      setIsTerminatingAll(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadActiveCalls();
    }
  }, [user, loadActiveCalls]);

  useEffect(() => {
    const interval = setInterval(() => {
      setLiveCalls(prev => prev.map(call => {
        if (call.start_time) {
          const startMs = new Date(call.start_time).getTime();
          const nowMs = Date.now();
          const newDuration = Math.floor((nowMs - startMs) / 1000);
          return { ...call, duration: newDuration };
        }
        return call;
      }));
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!user?.tenant_id || !isConnected) return;

    const callsRoom = `tenant:${user?.tenant_id}:calls`;
    joinRoom(callsRoom);

    const handleCallsUpdate = (calls: LiveCall[]) => {
      setLiveCalls(calls);
      setIsLoading(false);
    };

    const handleCallCreated = (call: LiveCall) => {
      setLiveCalls((prev) => {
        if (prev.some(c => c.uuid === call.uuid)) return prev;
        return [...prev, call];
      });
      toast({
        title: "Nuova Chiamata",
        description: `Chiamata in arrivo da ${call.caller_number}`,
      });
    };

    const handleCallUpdated = (call: LiveCall) => {
      setLiveCalls((prev) =>
        prev.map((c) => (c.uuid === call.uuid ? call : c))
      );
    };

    const handleCallAnswered = (call: LiveCall) => {
      setLiveCalls((prev) =>
        prev.map((c) => (c.uuid === call.uuid ? { ...c, ...call, status: 'answered' } : c))
      );
    };

    const handleCallBridged = (call: LiveCall) => {
      setLiveCalls((prev) =>
        prev.map((c) => (c.uuid === call.uuid ? { ...c, ...call, status: 'bridged' } : c))
      );
    };

    const handleCallEnded = ({ uuid }: { uuid: string }) => {
      setLiveCalls((prev) => prev.filter((c) => c.uuid !== uuid));
    };

    const handleActionSuccess = ({ uuid, action }: { uuid: string; action: string }) => {
      toast({
        title: "Azione Riuscita",
        description: `${action} eseguita sulla chiamata`,
      });
    };

    const handleCallError = ({ message }: { message: string }) => {
      toast({
        title: "Errore",
        description: message,
        variant: "destructive",
      });
    };

    const handleCardTriggered = (event: CardTriggerEvent) => {
      setTriggeredCards(prev => [event, ...prev].slice(0, 50));
      toast({
        title: `Coaching: ${event.card.title}`,
        description: `Keyword "${event.matchedKeyword}" rilevata`,
      });
    };

    const handleSentimentUpdate = (event: SentimentUpdateEvent) => {
      setCallSentiments(prev => {
        const next = new Map(prev);
        const existing = next.get(event.callUuid) || { score: 0.5, label: 'neutral' as const, history: [] };
        const wasCritical = existing.label === 'critical';
        existing.score = event.score;
        existing.label = event.label;
        existing.history = [...existing.history, { score: event.score, timestamp: event.timestamp }].slice(-20);
        next.set(event.callUuid, existing);
        if (event.label === 'critical' && !wasCritical) {
          const call = liveCalls.find(c => c.uuid === event.callUuid);
          toast({
            title: "Sentiment Critico",
            description: `Chiamata ${call?.caller_number || event.callUuid.substring(0, 8)} — intervento necessario`,
            variant: "destructive",
          });
        }
        return next;
      });
    };

    on('calls:update', handleCallsUpdate);
    on('call:created', handleCallCreated);
    on('call:updated', handleCallUpdated);
    on('call:answered', handleCallAnswered);
    on('call:bridged', handleCallBridged);
    on('call:ended', handleCallEnded);
    on('call:actionSuccess', handleActionSuccess);
    on('call:error', handleCallError);
    on('coaching:card_triggered', handleCardTriggered);
    on('coaching:sentiment_update', handleSentimentUpdate);

    return () => {
      leaveRoom(callsRoom);
      off('calls:update', handleCallsUpdate);
      off('call:created', handleCallCreated);
      off('call:updated', handleCallUpdated);
      off('call:answered', handleCallAnswered);
      off('call:bridged', handleCallBridged);
      off('call:ended', handleCallEnded);
      off('call:actionSuccess', handleActionSuccess);
      off('call:error', handleCallError);
      off('coaching:card_triggered', handleCardTriggered);
      off('coaching:sentiment_update', handleSentimentUpdate);
    };
  }, [user?.tenant_id, isConnected, socketVersion, on, off, joinRoom, leaveRoom, toast, liveCalls]);

  const handleCallAction = async (callUuid: string, action: string) => {
    try {
      if (!isConnected || !user?.tenant_id) {
        throw new Error('Non connesso al WebSocket');
      }
      emit('call:action', { uuid: callUuid, action, tenantId: user.tenant_id });
    } catch (error) {
      toast({
        title: "Azione fallita",
        description: getErrorMessage(error) || `Impossibile eseguire l'azione ${action}`,
        variant: "destructive",
      });
    }
  };

  const loadRTACards = useCallback(async () => {
    if (!user || !hasCoachingPermission) return;
    setIsLoadingCards(true);
    try {
      const data = await apiClient.get<RTACard[]>('/coaching/cards');
      if (data.success && data.data) {
        setRtaCards(data.data);
      }
    } catch (err) {
      console.error('[Coaching] Failed to load cards:', err);
    } finally {
      setIsLoadingCards(false);
    }
  }, [user, hasCoachingPermission]);

  const loadCoachingStats = useCallback(async () => {
    if (!user || !hasCoachingPermission) return;
    try {
      const data = await apiClient.get<CoachingStatsData>('/coaching/stats');
      if (data.success && data.data) {
        setCoachingStats(data.data);
      }
    } catch (err) {
      console.error('[Coaching] Failed to load stats:', err);
    }
  }, [user, hasCoachingPermission]);

  const [trendData, setTrendData] = useState<TrendData | null>(null);

  const loadTrendData = useCallback(async () => {
    if (!user || !hasCoachingPermission) return;
    try {
      const data = await apiClient.get<TrendData>('/coaching/stats/trend');
      if (data.success && data.data) {
        setTrendData(data.data);
      }
    } catch (err) {
      console.error('[Coaching] Failed to load trend data:', err);
    }
  }, [user, hasCoachingPermission]);

  const loadSentimentSettings = useCallback(async () => {
    if (!user || !hasCoachingPermission) return;
    try {
      const data = await apiClient.get<CoachingSentimentSettings>('/coaching/settings');
      if (data.success && data.data) {
        setSentimentSettings(data.data);
      }
    } catch (err) {
      console.error('[Coaching] Failed to load sentiment settings:', err);
    }
  }, [user, hasCoachingPermission]);

  const saveSentimentSettings = async () => {
    if (!hasCoachingPermission) return;
    setIsSavingSettings(true);
    try {
      const data = await apiClient.put<CoachingSentimentSettings>('/coaching/settings', sentimentSettings);
      if (data.success && data.data) {
        setSentimentSettings(data.data);
        toast({ title: 'Salvato', description: 'Configurazione sentiment aggiornata' });
      } else {
        throw new Error(data.error);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Errore sconosciuto';
      toast({ title: 'Errore', description: msg, variant: 'destructive' });
    } finally {
      setIsSavingSettings(false);
    }
  };

  const resetForm = () => {
    setCardForm({
      title: '', content: '', category: 'general', priority: 1,
      keywords: '', keyword_mode: 'any', language: 'it', cooldown_seconds: 60,
      rag_enabled: false, target_queues_text: '',
    });
  };

  const saveCard = async () => {
    if (!hasCoachingPermission) return;
    if (!cardForm.title || !cardForm.content || !cardForm.keywords) {
      toast({ title: 'Errore', description: 'Compila tutti i campi obbligatori', variant: 'destructive' });
      return;
    }

    const keywordsArray = cardForm.keywords.split(',').map(k => k.trim()).filter(Boolean);
    if (keywordsArray.length === 0) {
      toast({ title: 'Errore', description: 'Inserisci almeno una keyword', variant: 'destructive' });
      return;
    }

    try {
      const target_queues = cardForm.target_queues_text.split(',').map(q => q.trim()).filter(Boolean);
      const { target_queues_text: _discarded, ...formWithoutText } = cardForm;
      const body = { ...formWithoutText, keywords: keywordsArray, target_queues };
      const data = editingCard
        ? await apiClient.put<RTACard>(`/coaching/cards/${editingCard.id}`, body)
        : await apiClient.post<RTACard>('/coaching/cards', body);
      if (data.success) {
        toast({ title: editingCard ? 'Card aggiornata' : 'Card creata', description: data.data?.title });
        setEditingCard(null);
        setIsCreating(false);
        resetForm();
        loadRTACards();
      } else {
        throw new Error(data.error);
      }
    } catch (err: unknown) {
      toast({ title: 'Errore', description: err instanceof Error ? err.message : 'Errore sconosciuto', variant: 'destructive' });
    }
  };

  const deleteCard = async (id: string) => {
    if (!hasCoachingPermission) return;
    try {
      const data = await apiClient.delete(`/coaching/cards/${id}`);
      if (data.success) {
        toast({ title: 'Card eliminata' });
        loadRTACards();
      }
    } catch (err: unknown) {
      toast({ title: 'Errore', description: err instanceof Error ? err.message : 'Errore sconosciuto', variant: 'destructive' });
    }
  };

  const toggleCardActive = async (card: RTACard) => {
    if (!hasCoachingPermission) return;
    try {
      const data = await apiClient.put(`/coaching/cards/${card.id}`, { is_active: !card.is_active });
      if (data.success) {
        loadRTACards();
      }
    } catch (err: unknown) {
      toast({ title: 'Errore', description: err instanceof Error ? err.message : 'Errore sconosciuto', variant: 'destructive' });
    }
  };

  const startEditing = (card: RTACard) => {
    setEditingCard(card);
    setIsCreating(false);
    setCardForm({
      title: card.title,
      content: card.content,
      category: card.category,
      priority: card.priority,
      keywords: card.keywords.join(', '),
      keyword_mode: card.keyword_mode,
      language: card.language,
      cooldown_seconds: card.cooldown_seconds,
      rag_enabled: card.rag_enabled || false,
      target_queues_text: (card.target_queues || []).join(', '),
    });
  };

  const renderMarkdown = (text: string) => {
    const sanitized = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    const html = sanitized
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/`(.+?)`/g, '<code class="bg-muted px-1 rounded text-xs">$1</code>')
      .replace(/^[-*]\s+(.+)$/gm, '<li class="ml-4">$1</li>')
      .replace(/\n/g, '<br/>');
    return <div className="text-sm text-muted-foreground mb-2 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: html }} />;
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getDirectionIcon = (direction: string) => {
    switch (direction) {
      case 'inbound': return <PhoneIncoming className="h-5 w-5 text-green-600" />;
      case 'outbound': return <PhoneOutgoing className="h-5 w-5 text-orange-600" />;
      case 'internal': return <Phone className="h-5 w-5 text-purple-600" />;
      default: return <Phone className="h-5 w-5 text-gray-600" />;
    }
  };

  const getDirectionBadge = (direction: string) => {
    switch (direction) {
      case 'inbound': return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Inbound</Badge>;
      case 'outbound': return <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100">Outbound</Badge>;
      case 'internal': return <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100">Internal</Badge>;
      default: return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const getStatusBadge = (status: string, queueName?: string) => {
    const isQueueCall = !!queueName;
    switch (status) {
      case 'ringing': return <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100 animate-pulse">Squilla</Badge>;
      case 'answered':
        return isQueueCall 
          ? <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100">In Coda</Badge>
          : <Badge className="bg-green-100 text-green-700 hover:bg-green-100">In Corso</Badge>;
      case 'bridged': return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">In Corso</Badge>;
      case 'on_hold': return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">In Attesa</Badge>;
      case 'muted': return <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100">Silenziata</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getSentimentIcon = (label: string) => {
    switch (label) {
      case 'positive': return <TrendingUp className="h-4 w-4 text-green-500" />;
      case 'critical': return <AlertTriangle className="h-4 w-4 text-red-600 animate-pulse" />;
      case 'negative': return <TrendingDown className="h-4 w-4 text-red-500" />;
      default: return <Minus className="h-4 w-4 text-gray-400" />;
    }
  };

  const getSentimentColor = (label: string) => {
    switch (label) {
      case 'positive': return 'bg-green-100 text-green-700';
      case 'critical': return 'bg-red-200 text-red-800 border border-red-400';
      case 'negative': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getSentimentBarColor = (score: number) => {
    if (score >= 0.6) return 'bg-green-500';
    if (score <= 0.2) return 'bg-red-700';
    if (score <= 0.4) return 'bg-red-500';
    return 'bg-yellow-500';
  };

  const inboundCount = liveCalls.filter(call => call.direction === 'inbound').length;
  const outboundCount = liveCalls.filter(call => call.direction === 'outbound').length;
  const internalCount = liveCalls.filter(call => call.direction === 'internal').length;

  const renderCallCard = (call: LiveCall) => {
    const sentiment = callSentiments.get(call.uuid);
    return (
      <Card key={call.uuid} className="overflow-hidden hover:shadow-lg transition-shadow duration-200">
        <CardContent className="p-0">
          <div className="flex flex-col md:flex-row">
            <div className="flex-1 p-5">
              <div className="flex items-start gap-4">
                <div className={`h-12 w-12 rounded-lg flex items-center justify-center ${
                  call.direction === 'inbound' ? 'bg-green-100 dark:bg-green-900/30' :
                  call.direction === 'outbound' ? 'bg-orange-100 dark:bg-orange-900/30' :
                  'bg-purple-100 dark:bg-purple-900/30'
                }`}>
                  {getDirectionIcon(call.direction)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg font-semibold text-foreground truncate">{call.caller_number}</span>
                    <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <span className="text-lg font-semibold text-foreground truncate">{call.callee_number}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    {call.caller_name && <span>{call.caller_name}</span>}
                    {call.caller_name && call.callee_name && <ArrowRight className="h-3 w-3" />}
                    {call.callee_name && <span>{call.callee_name}</span>}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {getDirectionBadge(call.direction)}
                    {getStatusBadge(call.status, call.queue_name ?? undefined)}
                    {call.recording && (
                      <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
                        <Circle className="h-2 w-2 mr-1 fill-red-500 animate-pulse" />
                        Recording
                      </Badge>
                    )}
                    {sentiment && (
                      <Badge className={getSentimentColor(sentiment.label)}>
                        {getSentimentIcon(sentiment.label)}
                        <span className="ml-1">{Math.round(sentiment.score * 100)}%</span>
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col md:border-l border-t md:border-t-0 border-border">
              <div className="p-5 bg-muted/30 flex items-center gap-3">
                <Clock className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-2xl font-mono font-bold text-foreground">{formatDuration(call.duration)}</p>
                  <p className="text-xs text-muted-foreground">Iniziata: {new Date(call.start_time).toLocaleTimeString()}</p>
                </div>
              </div>
              <div className="p-3 flex flex-wrap gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => handleCallAction(call.uuid, 'hold')}
                  className="text-blue-600 border-blue-200 hover:bg-blue-50 dark:hover:bg-blue-900/20">
                  <Pause className="h-4 w-4 mr-1" /> Hold
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleCallAction(call.uuid, 'mute')}
                  className={call.status === 'muted' 
                    ? "text-yellow-600 border-yellow-200 hover:bg-yellow-50 dark:hover:bg-yellow-900/20" 
                    : "text-gray-600 border-gray-200 hover:bg-gray-50 dark:hover:bg-gray-900/20"}>
                  {call.status === 'muted' ? <MicOff className="h-4 w-4 mr-1" /> : <Mic className="h-4 w-4 mr-1" />}
                  {call.status === 'muted' ? 'Unmute' : 'Mute'}
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleCallAction(call.uuid, 'record')}
                  className={call.recording 
                    ? "text-red-600 border-red-200 hover:bg-red-50 dark:hover:bg-red-900/20" 
                    : "text-gray-600 border-gray-200 hover:bg-gray-50 dark:hover:bg-gray-900/20"}>
                  {call.recording ? <Square className="h-4 w-4 mr-1" /> : <Radio className="h-4 w-4 mr-1" />}
                  {call.recording ? 'Stop Rec' : 'Record'}
                </Button>
                <Button variant="destructive" size="sm" onClick={() => handleCallAction(call.uuid, 'hangup')}>
                  <PhoneOff className="h-4 w-4 mr-1" /> Hangup
                </Button>
              </div>
            </div>
          </div>

          {sentiment && sentiment.history.length > 1 && (
            <div className="px-5 pb-3">
              <div className="flex items-center gap-2 mb-1">
                <Brain className="h-3 w-3 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Sentiment</span>
              </div>
              <div className="h-2 rounded-full bg-muted overflow-hidden flex gap-px">
                {sentiment.history.slice(-15).map((h, i) => (
                  <div key={i} className={`flex-1 rounded-sm ${getSentimentBarColor(h.score)}`} />
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderCoachingView = () => (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Card Attivate</p>
                <p className="text-3xl font-bold text-blue-600">{triggeredCards.length}</p>
                <p className="text-xs text-muted-foreground mt-1">In questa sessione</p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Zap className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Sentiment Medio</p>
                <p className="text-3xl font-bold text-green-600">
                  {callSentiments.size > 0
                    ? Math.round(Array.from(callSentiments.values()).reduce((a, b) => a + b.score, 0) / callSentiments.size * 100) + '%'
                    : '-'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">Chiamate monitorate: {callSentiments.size}</p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Chiamate Attive con AI</p>
                <p className="text-3xl font-bold text-orange-600">{callSentiments.size}</p>
                <p className="text-xs text-muted-foreground mt-1">Con analisi sentiment</p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                <Brain className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {callSentiments.size > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-3">Sentiment Chiamate in Corso</h3>
          <div className="grid gap-3">
            {Array.from(callSentiments.entries()).map(([callUuid, state]) => {
              const call = liveCalls.find(c => c.uuid === callUuid);
              return (
                <Card key={callUuid} className="overflow-hidden">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {getSentimentIcon(state.label)}
                        <div>
                          <p className="font-medium">
                            {call ? `${call.caller_number} → ${call.callee_number}` : callUuid.substring(0, 12)}
                          </p>
                          <p className="text-xs text-muted-foreground capitalize">{state.label}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-32 h-3 rounded-full bg-muted overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${getSentimentBarColor(state.score)}`}
                            style={{ width: `${state.score * 100}%` }}
                          />
                        </div>
                        <span className="text-sm font-mono font-bold w-12 text-right">
                          {Math.round(state.score * 100)}%
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <h3 className="text-lg font-semibold mb-3">Suggerimenti Attivati</h3>
        {triggeredCards.length > 0 ? (
          <div className="grid gap-3">
            {triggeredCards.map((tc, idx) => {
              const call = liveCalls.find(c => c.uuid === tc.callUuid);
              return (
                <Card key={idx} className={`border-l-4 ${
                  tc.card.priority >= 4 ? 'border-l-red-500' :
                  tc.card.priority >= 3 ? 'border-l-orange-500' :
                  'border-l-blue-500'
                }`}>
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Zap className="h-4 w-4 text-amber-500" />
                          <span className="font-semibold">{tc.card.title}</span>
                          <Badge variant="outline" className="text-xs">{tc.card.category}</Badge>
                          <Badge variant="outline" className="text-xs">P{tc.card.priority}</Badge>
                        </div>
                        {renderMarkdown(tc.card.content_markdown || tc.card.content)}
                        {tc.ragContext && (
                          <div className="text-xs bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded p-2 mb-2">
                            <span className="font-semibold text-blue-700 dark:text-blue-400">KB: </span>
                            {tc.ragContext}
                          </div>
                        )}
                        <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
                          <span>Keyword: <strong>{tc.matchedKeyword}</strong></span>
                          {call && <span>Chiamata: {call.caller_number}</span>}
                          <span>{new Date(tc.timestamp).toLocaleTimeString()}</span>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => {
                            navigator.clipboard.writeText(tc.card.content_markdown || tc.card.content);
                            toast({ title: 'Copiato', description: 'Suggerimento copiato negli appunti' });
                          }}>
                            <MessageSquare className="h-3 w-3 mr-1" /> Copia Risposta
                          </Button>
                          {call && (
                            <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => {
                              emit('call:whisper', { uuid: call.uuid, message: tc.card.content });
                            }}>
                              <Mic className="h-3 w-3 mr-1" /> Whisper
                            </Button>
                          )}
                          {call && (
                            <Button size="sm" variant="outline" className="text-xs h-7 text-orange-600" onClick={() => {
                              emit('call:transfer', { uuid: call.uuid });
                            }}>
                              <ArrowRight className="h-3 w-3 mr-1" /> Trasferisci
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <MessageSquare className="h-10 w-10 text-muted-foreground mb-4" />
              <p className="text-muted-foreground text-center">
                Nessun suggerimento attivato. Le card RTA appariranno qui quando le keyword configurate
                vengono rilevate nelle conversazioni in corso.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );

  const renderCardForm = () => (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{editingCard ? 'Modifica Card' : 'Nuova Card RTA'}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label>Titolo *</Label>
            <Input value={cardForm.title} onChange={e => setCardForm(p => ({ ...p, title: e.target.value }))} placeholder="es. Richiesta Rimborso" />
          </div>
          <div>
            <Label>Categoria</Label>
            <Input value={cardForm.category} onChange={e => setCardForm(p => ({ ...p, category: e.target.value }))} placeholder="general" />
          </div>
        </div>
        <div>
          <Label>Contenuto / Suggerimento *</Label>
          <textarea
            className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={cardForm.content}
            onChange={e => setCardForm(p => ({ ...p, content: e.target.value }))}
            placeholder="Suggerimento da mostrare al supervisore quando la keyword viene rilevata..."
          />
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <Label>Keywords * (separate da virgola)</Label>
            <Input value={cardForm.keywords} onChange={e => setCardForm(p => ({ ...p, keywords: e.target.value }))} placeholder="rimborso, cancellazione, reclamo" />
          </div>
          <div>
            <Label>Modalità Match</Label>
            <select
              className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={cardForm.keyword_mode}
              onChange={e => setCardForm(p => ({ ...p, keyword_mode: e.target.value as 'any' | 'all' | 'exact' | 'regex' }))}
            >
              <option value="any">Qualsiasi (parziale)</option>
              <option value="exact">Esatta (parola intera)</option>
              <option value="all">Tutte le keyword</option>
              <option value="regex">Regex</option>
            </select>
          </div>
          <div>
            <Label>Lingua</Label>
            <select
              className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={cardForm.language}
              onChange={e => setCardForm(p => ({ ...p, language: e.target.value }))}
            >
              <option value="it">Italiano</option>
              <option value="en">English</option>
            </select>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <Label>Priorità (1-5)</Label>
            <Input type="number" min={1} max={5} value={cardForm.priority} onChange={e => setCardForm(p => ({ ...p, priority: parseInt(e.target.value) || 1 }))} />
          </div>
          <div>
            <Label>Cooldown (secondi)</Label>
            <Input type="number" min={10} value={cardForm.cooldown_seconds} onChange={e => setCardForm(p => ({ ...p, cooldown_seconds: parseInt(e.target.value) || 60 }))} />
          </div>
          <div>
            <Label>RAG / Knowledge Base</Label>
            <div className="flex items-center gap-2 h-10">
              <input
                type="checkbox"
                id="rag_enabled"
                checked={cardForm.rag_enabled || false}
                onChange={e => setCardForm(p => ({ ...p, rag_enabled: e.target.checked }))}
                className="rounded border-gray-300"
              />
              <Label htmlFor="rag_enabled" className="text-sm font-normal">Abilita arricchimento RAG</Label>
            </div>
          </div>
          <div>
            <Label>Code Target (opzionale)</Label>
            <Input
              value={cardForm.target_queues_text}
              onChange={e => setCardForm(p => ({ ...p, target_queues_text: e.target.value }))}
              placeholder="coda1, coda2"
            />
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={() => { setEditingCard(null); setIsCreating(false); resetForm(); }}>
            <X className="h-4 w-4 mr-1" /> Annulla
          </Button>
          <Button onClick={saveCard}>
            <Save className="h-4 w-4 mr-1" /> {editingCard ? 'Aggiorna' : 'Crea'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderCoachingSettings = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="h-5 w-5" /> Configurazione Sentiment
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Modalità Analisi</Label>
              <select
                className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={sentimentSettings.sentiment_mode}
                onChange={e => setSentimentSettings(s => ({ ...s, sentiment_mode: e.target.value }))}
              >
                <option value="dictionary">Dizionario (base)</option>
                <option value="advanced" disabled>Avanzata (LLM) — prossimamente</option>
              </select>
            </div>
            <div>
              <Label>Intervallo Emissione (secondi)</Label>
              <Input
                type="number" min={1} max={60}
                value={sentimentSettings.emit_interval_seconds}
                onChange={e => setSentimentSettings(s => ({ ...s, emit_interval_seconds: Number(e.target.value) }))}
              />
            </div>
            <div>
              <Label>Soglia Warning (score ≤)</Label>
              <Input
                type="number" min={0.1} max={0.9} step={0.05}
                value={sentimentSettings.warning_threshold}
                onChange={e => setSentimentSettings(s => ({ ...s, warning_threshold: Number(e.target.value) }))}
              />
              <p className="text-xs text-muted-foreground mt-1">Score sotto questa soglia = sentiment negativo</p>
            </div>
            <div>
              <Label>Soglia Critico (score ≤)</Label>
              <Input
                type="number" min={0.05} max={0.5} step={0.05}
                value={sentimentSettings.critical_threshold}
                onChange={e => setSentimentSettings(s => ({ ...s, critical_threshold: Number(e.target.value) }))}
              />
              <p className="text-xs text-muted-foreground mt-1">Score sotto questa soglia = alert critico al supervisore</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="notify_critical"
              checked={sentimentSettings.notify_on_critical}
              onChange={e => setSentimentSettings(s => ({ ...s, notify_on_critical: e.target.checked }))}
              className="rounded border-gray-300"
            />
            <Label htmlFor="notify_critical">Notifica al supervisore quando il sentiment diventa critico</Label>
          </div>
          <div className="flex justify-end">
            <Button onClick={saveSentimentSettings} disabled={isSavingSettings}>
              {isSavingSettings ? 'Salvataggio...' : 'Salva Configurazione'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Card RTA</h3>
          <p className="text-sm text-muted-foreground">
            Configura le card di coaching che appariranno quando specifiche keyword vengono rilevate
          </p>
        </div>
        {!isCreating && !editingCard && (
          <Button onClick={() => { setIsCreating(true); setEditingCard(null); resetForm(); }}>
            <Plus className="h-4 w-4 mr-1" /> Nuova Card
          </Button>
        )}
      </div>

      {(isCreating || editingCard) && renderCardForm()}

      {isLoadingCards ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
        </div>
      ) : rtaCards.length > 0 ? (
        <div className="grid gap-3">
          {rtaCards.map(card => (
            <Card key={card.id} className={`transition-opacity ${!card.is_active ? 'opacity-60' : ''}`}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold">{card.title}</span>
                      <Badge variant="outline" className="text-xs">{card.category}</Badge>
                      <Badge variant="outline" className="text-xs">P{card.priority}</Badge>
                      <Badge className={card.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}>
                        {card.is_active ? 'Attiva' : 'Disattivata'}
                      </Badge>
                    </div>
                    {renderMarkdown(card.content_markdown || card.content)}
                    <div className="flex flex-wrap gap-1">
                      {card.keywords.map((kw, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">{kw}</Badge>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Modalità: {card.keyword_mode} | Lingua: {card.language} | Cooldown: {card.cooldown_seconds}s
                    </p>
                  </div>
                  <div className="flex gap-1 ml-4">
                    <Button variant="ghost" size="sm" onClick={() => toggleCardActive(card)} title={card.is_active ? 'Disattiva' : 'Attiva'}>
                      {card.is_active ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => startEditing(card)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => deleteCard(card.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Settings className="h-10 w-10 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">
              Nessuna card RTA configurata. Crea la prima card per iniziare a ricevere
              suggerimenti in tempo reale durante le chiamate.
            </p>
          </CardContent>
        </Card>
      )}

      {coachingStats && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="h-5 w-5" /> Statistiche Coaching (ultimi {coachingStats.period_days} giorni)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-4">
                <div>
                  <p className="text-sm text-muted-foreground">Trigger Totali</p>
                  <p className="text-2xl font-bold">{coachingStats.triggers?.total_triggers || 0}</p>
                  <p className="text-xs text-muted-foreground">{coachingStats.triggers?.calls_with_triggers || 0} chiamate coinvolte</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Sentiment Medio</p>
                  <p className="text-2xl font-bold">
                    {coachingStats.sentiment?.avg_score ? `${Math.round(Number(coachingStats.sentiment.avg_score) * 100)}%` : '-'}
                  </p>
                  <p className="text-xs text-muted-foreground">{coachingStats.sentiment?.total_logs || 0} analisi</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Distribuzione</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    <Badge className="bg-green-100 text-green-700">{coachingStats.sentiment?.positive_count || 0} pos</Badge>
                    <Badge className="bg-gray-100 text-gray-600">{coachingStats.sentiment?.neutral_count || 0} neu</Badge>
                    <Badge className="bg-red-100 text-red-700">{coachingStats.sentiment?.negative_count || 0} neg</Badge>
                    <Badge className="bg-red-200 text-red-800 animate-pulse">{coachingStats.sentiment?.critical_count || 0} crit</Badge>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Top Card Triggerate</p>
                  <div className="space-y-1 mt-1">
                    {(coachingStats.top_cards || []).slice(0, 5).map(tc => (
                      <div key={tc.id} className="flex justify-between text-xs">
                        <span className="truncate">{tc.title}</span>
                        <Badge variant="outline" className="text-xs ml-1">{tc.trigger_count}</Badge>
                      </div>
                    ))}
                    {(!coachingStats.top_cards || coachingStats.top_cards.length === 0) && (
                      <p className="text-xs text-muted-foreground">Nessun trigger registrato</p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {coachingStats.top_keywords && coachingStats.top_keywords.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Zap className="h-5 w-5" /> Top Keywords Triggerate
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {coachingStats.top_keywords.map((kw, i) => (
                    <Badge key={i} variant="outline" className="text-sm py-1 px-3">
                      {kw.matched_keyword} <span className="ml-1 font-bold text-primary">×{kw.hit_count}</span>
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {trendData && (
        <>
          {trendData.sentiment_trend.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" /> Trend Sentiment per Operatore/Coda
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-2">Giorno</th>
                        <th className="text-left py-2 px-2">Ora</th>
                        <th className="text-left py-2 px-2">Operatore</th>
                        <th className="text-left py-2 px-2">Coda</th>
                        <th className="text-right py-2 px-2">Score Medio</th>
                        <th className="text-right py-2 px-2">Analisi</th>
                        <th className="text-right py-2 px-2">Critici</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trendData.sentiment_trend.slice(0, 50).map((row, i) => (
                        <tr key={i} className="border-b hover:bg-muted/50">
                          <td className="py-1.5 px-2">{new Date(row.day).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })}</td>
                          <td className="py-1.5 px-2">{String(row.hour).padStart(2, '0')}:00</td>
                          <td className="py-1.5 px-2">{row.agent_extension || '-'}</td>
                          <td className="py-1.5 px-2">{row.queue_id ? row.queue_id.substring(0, 8) : '-'}</td>
                          <td className="py-1.5 px-2 text-right">
                            <span className={`font-mono ${Number(row.avg_score) >= 0.6 ? 'text-green-600' : Number(row.avg_score) <= 0.3 ? 'text-red-600' : 'text-gray-600'}`}>
                              {Math.round(Number(row.avg_score) * 100)}%
                            </span>
                          </td>
                          <td className="py-1.5 px-2 text-right">{row.log_count}</td>
                          <td className="py-1.5 px-2 text-right">{row.critical_count > 0 ? <span className="text-red-600 font-bold">{row.critical_count}</span> : '0'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {trendData.trigger_heatmap.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Activity className="h-5 w-5" /> Heatmap Trigger Oraria
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-25 gap-0.5 text-[10px]">
                  <div className="col-span-1" />
                  {Array.from({ length: 24 }, (_, h) => (
                    <div key={h} className="text-center text-muted-foreground">{h}</div>
                  ))}
                  {['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'].map((dayName, dow) => (
                    <>
                      <div key={`label-${dow}`} className="text-right pr-1 text-muted-foreground leading-5">{dayName}</div>
                      {Array.from({ length: 24 }, (_, h) => {
                        const cell = trendData.trigger_heatmap.find(c => c.day_of_week === dow && c.hour === h);
                        const count = cell?.trigger_count || 0;
                        const maxCount = Math.max(...trendData.trigger_heatmap.map(c => c.trigger_count), 1);
                        const intensity = count / maxCount;
                        return (
                          <div
                            key={`${dow}-${h}`}
                            className="h-5 rounded-sm border border-border"
                            style={{ backgroundColor: count > 0 ? `rgba(239, 68, 68, ${0.15 + intensity * 0.75})` : 'transparent' }}
                            title={`${dayName} ${h}:00 — ${count} trigger`}
                          />
                        );
                      })}
                    </>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-background text-foreground p-6 animate-fade-in-up space-y-8 pb-20 font-sans selection:bg-primary/30">
      
      <div className="relative flex justify-between items-center z-10">
        <div>
          <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
            Chiamate Live
          </h1>
          <p className="text-muted-foreground mt-1">
            Monitora le chiamate attive in tempo reale
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isLoading ? (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-100 dark:bg-yellow-900/30 rounded-full">
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-yellow-600"></div>
              <span className="text-sm font-medium text-yellow-700 dark:text-yellow-400">Connessione...</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 dark:bg-green-900/30 rounded-full">
              <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-green-700 dark:text-green-400">Live</span>
            </div>
          )}
          <Button variant="outline" size="sm" 
            onClick={() => {
              if (isConnected && user?.tenant_id) {
                emit('calls:refresh', { tenantId: user.tenant_id });
                toast({ title: "Aggiornamento", description: "Richiesta aggiornamento chiamate inviata" });
              }
            }}
            disabled={!isConnected}
            className="text-muted-foreground hover:text-foreground">
            <RefreshCw className="h-4 w-4 mr-2" /> Aggiorna
          </Button>
          
          {user?.role === 'admin' && liveCalls.length > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm" disabled={isTerminatingAll} className="bg-red-600 hover:bg-red-700">
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  {isTerminatingAll ? 'Terminando...' : 'Termina Tutte'}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-500" />
                    Terminare tutte le chiamate?
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    Stai per terminare forzatamente <strong>{liveCalls.length}</strong> chiamate attive.
                    Questa azione non può essere annullata.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annulla</AlertDialogCancel>
                  <AlertDialogAction onClick={handleTerminateAllCalls} className="bg-red-600 hover:bg-red-700">
                    Sì, termina tutte
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <Tabs defaultValue="live" onValueChange={(val) => {
        if (val === 'settings' && hasCoachingPermission) {
          loadRTACards(); loadCoachingStats(); loadTrendData(); loadSentimentSettings();
        }
      }}>
        <TabsList>
          <TabsTrigger value="live" className="gap-1">
            <Phone className="h-4 w-4" /> Chiamate Live
          </TabsTrigger>
          {hasCoachingPermission && (
            <TabsTrigger value="coaching" className="gap-1">
              <Brain className="h-4 w-4" /> Coaching View
            </TabsTrigger>
          )}
          {hasCoachingPermission && (
            <TabsTrigger value="settings" className="gap-1">
              <Settings className="h-4 w-4" /> Coaching Settings
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="live" className="space-y-6">
          <div className="grid gap-4 md:grid-cols-4">
            <Card className="border-l-4 border-l-blue-500 bg-gradient-to-r from-blue-50/50 to-transparent dark:from-blue-900/10">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Totale Attive</p>
                    <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{liveCalls.length}</p>
                    <p className="text-xs text-muted-foreground mt-1">Chiamate in corso</p>
                  </div>
                  <div className="h-12 w-12 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <Phone className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-green-500 bg-gradient-to-r from-green-50/50 to-transparent dark:from-green-900/10">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">In Entrata</p>
                    <p className="text-3xl font-bold text-green-600 dark:text-green-400">{inboundCount}</p>
                    <p className="text-xs text-muted-foreground mt-1">Chiamate inbound</p>
                  </div>
                  <div className="h-12 w-12 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                    <PhoneIncoming className="h-6 w-6 text-green-600 dark:text-green-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-orange-500 bg-gradient-to-r from-orange-50/50 to-transparent dark:from-orange-900/10">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">In Uscita</p>
                    <p className="text-3xl font-bold text-orange-600 dark:text-orange-400">{outboundCount}</p>
                    <p className="text-xs text-muted-foreground mt-1">Chiamate outbound</p>
                  </div>
                  <div className="h-12 w-12 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                    <PhoneOutgoing className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-purple-500 bg-gradient-to-r from-purple-50/50 to-transparent dark:from-purple-900/10">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Interne</p>
                    <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">{internalCount}</p>
                    <p className="text-xs text-muted-foreground mt-1">Chiamate interne</p>
                  </div>
                  <div className="h-12 w-12 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                    <Phone className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {liveCalls.length > 0 ? (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-foreground">Chiamate Attive</h2>
              <div className="grid gap-4">
                {liveCalls.map(renderCallCard)}
              </div>
            </div>
          ) : (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <div className="h-20 w-20 rounded-full bg-muted/50 flex items-center justify-center mb-6">
                  <PhoneOff className="h-10 w-10 text-muted-foreground" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">Nessuna chiamata attiva</h3>
                <p className="text-muted-foreground text-center max-w-md">
                  Al momento non ci sono chiamate in corso nel sistema. 
                  Le chiamate appariranno qui in tempo reale.
                </p>
                <div className="flex items-center gap-2 mt-6 text-sm text-muted-foreground">
                  <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span>Monitoraggio attivo</span>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {hasCoachingPermission && (
          <TabsContent value="coaching">
            {renderCoachingView()}
          </TabsContent>
        )}

        {hasCoachingPermission && (
          <TabsContent value="settings">
            {renderCoachingSettings()}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
